import React, { useMemo } from 'react';
import * as yup from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import ControlledCheckbox from 'components/stepper/field-components/FormCheckboxGroup';
import { twMerge } from 'tailwind-merge';
import moment from 'moment/moment';
import { FULL_DATE, STANDARD_DATE } from 'utilities/dateHelper';
import ButtonComponent from 'components/button/buttonComponent';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFormErrors } from 'components/stepper/useStepperForm';
import {
  Persona_Inquiry_Status_Enum,
  useAcceptUserAgreementMutation,
  useGetAgreementSubstanceByUserIdQuery,
  UserAgreementExpirationType,
  UserAgreementTypes,
} from 'graphql/generated/hasura';
import { MY_ACCOUNT_PROFILE } from 'utilities/routes';
import { usePatientProfileContext } from 'app/my-account/pages/patient/PatientProfileContext';
import { useCustomToast } from 'layout/useToast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'auth/context/AuthContext';
import Loader from 'components/loaderComponent';
import { useGetComponent } from 'hooks/useGetComponent';
import { componentIds } from 'utilities/constants';
import { sanitize } from 'utilities/sanitizer';
import { Roles } from '../../firebase/interfaces';

const Block: React.FC<{ title?: string; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  return (
    <div className="flex flex-col gap-2.5">
      <h5 className="text-h4 font-semibold text-neutral-800">{title}</h5>
      <p className="flex flex-col gap-1.5 text-med-gray-3 font-medium">
        {children}
      </p>
    </div>
  );
};

const HasuraUserAgreementExpirationType: Record<
  number,
  UserAgreementExpirationType
> = {
  2: UserAgreementExpirationType.EndOfTreatment,
  1: UserAgreementExpirationType.Manual,
};

const ConsentToUseAndDiscloseSubstance = () => {
  const { user } = useAuth();
  const { updatePatientValidation } = usePatientProfileContext();
  const { addToast } = useCustomToast();
  const navigate = useNavigate();

  const { data: locale, loading } = useGetComponent({
    componentId: componentIds.SUBSTANCE_DOCK,
    locale: 'en',
  });

  const { onError } = useFormErrors();
  const substanceConsentSchema = yup.object().shape({
    [UserAgreementExpirationType.EndOfTreatment]: yup.boolean(),
    [UserAgreementExpirationType.Manual]: yup
      .boolean()
      .test(
        'exactly-one-expiration',
        'You must select exactly one expiration option in Chapter 7.',
        (value, context) => {
          const parent = context.parent as {
            [UserAgreementExpirationType.EndOfTreatment]: boolean;
            [UserAgreementExpirationType.Manual]: boolean;
          };
          const expires =
            parent?.[UserAgreementExpirationType.EndOfTreatment] === true;
          const noExpiration =
            parent?.[UserAgreementExpirationType.Manual] === true;
          return (expires && !noExpiration) || (!expires && noExpiration);
        },
      ),
    agreedToSudConsent: yup
      .boolean()
      .oneOf([true], 'You must acknowledge the terms to proceed.')
      .required(),
  });

  const [acceptUserAgreement] = useAcceptUserAgreementMutation({
    refetchQueries: ['GetAgreementsByUserId', 'GetAgreementSubstanceByUserId'],
    awaitRefetchQueries: true,
    onCompleted: () => {
      addToast({
        type: 'success',
        message:
          'Consent to use and disclose substance use disorder records successfully signed',
      });
      updatePatientValidation();
      navigate(MY_ACCOUNT_PROFILE);
    },
  });

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(substanceConsentSchema),
    values: {
      [UserAgreementExpirationType.EndOfTreatment]: false,
      [UserAgreementExpirationType.Manual]: false,
      agreedToSudConsent: false,
    },
  });

  const { data: userAgreements, loading: loadingAgreement } =
    useGetAgreementSubstanceByUserIdQuery({
      fetchPolicy: 'network-only',
      skip: user?.role !== Roles.PATIENT,
      variables: { id: user?.uuid || '' },
      onCompleted: (data) => {
        const isVerified =
          data?.user_agreements?.[0]?.status ===
          Persona_Inquiry_Status_Enum.Approved;
        if (isVerified) {
          methods.setValue('agreedToSudConsent', true);
          methods.setValue(
            HasuraUserAgreementExpirationType?.[
              data?.user_agreements?.[0]?.expiration_type
            ],
            true,
          );
        }
      },
    });

  const isVerified = useMemo(
    () =>
      userAgreements?.user_agreements?.[0]?.status ===
      Persona_Inquiry_Status_Enum.Approved,
    [userAgreements],
  );

  const onSubmit = (data: yup.InferType<typeof substanceConsentSchema>) => {
    const key = Object.entries(data).filter(
      ([key, value]) => !!value && key !== ' agreedToSudConsent',
    )?.[0][0];

    return acceptUserAgreement({
      variables: {
        agreementType: UserAgreementTypes.ConsentToUseAndDiscloseSudRecords,
        expiration: key as UserAgreementExpirationType,
      },
    });
  };

  const contents = useMemo(
    () => [
      locale?.whoUsed,
      locale?.informationCovered,
      locale?.recipients,
      locale?.disclosure,
      locale?.redisclosureNotice,
      locale?.rightToRevoke,
    ],
    [locale],
  );

  if (loadingAgreement || loading) {
    return <Loader />;
  }

  return (
    <div className="flex-wrap px-2.5 desktop:px-30">
      <h2 className="desktop:text-h1 text-h2 text-neutral-800 flex font-medium mb-30">
        {locale?.title ?? 'Consent to use and disclose substance'}
        <span className="text-alert-negative text-sm font-bold ml-auto">
          {locale?.required ?? '* Required'}
        </span>
      </h2>

      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit, onError)}
          className="flex flex-col gap-5"
        >
          {contents.map(({ title, content }, index) => (
            <Block title={`${index + 1}. ${title}`}>
              <div dangerouslySetInnerHTML={{ __html: sanitize(content) }} />
            </Block>
          ))}

          <Block title={`7. ${locale?.expiration?.title}`}>
            <>
              <ControlledCheckbox
                disabled={isVerified}
                isAlternativeColor
                name={UserAgreementExpirationType.EndOfTreatment}
                label={
                  locale?.expiration?.[
                    UserAgreementExpirationType.EndOfTreatment
                  ]
                }
              />
              <ControlledCheckbox
                disabled={isVerified}
                isAlternativeColor
                name={UserAgreementExpirationType.Manual}
                label={locale?.expiration?.[UserAgreementExpirationType.Manual]}
              />
            </>
          </Block>
          <Block title={`8. ${locale?.consequencesOfRefusingToSign?.title}`}>
            <div
              dangerouslySetInnerHTML={{
                __html: sanitize(locale?.consequencesOfRefusingToSign?.content),
              }}
            />
          </Block>
          <Block title={`9. ${locale?.signature?.title}`}>
            <>
              <p className={twMerge(isVerified && 'hidden')}>
                {locale?.signature?.date}: {moment().format(STANDARD_DATE)}
              </p>
              <ControlledCheckbox
                disabled={isVerified}
                isAlternativeColor
                name="agreedToSudConsent"
                label={locale?.signature?.agreedToSudConsent}
              />
            </>
          </Block>
          <ButtonComponent
            disabled={
              methods.formState.isSubmitting || !methods.formState.isDirty
            }
            showSpinner={methods.formState.isSubmitting}
            className={twMerge('!w-fit', isVerified && 'hidden')}
            htmlButtonType="submit"
            testID="accept"
          >
            Accept
          </ButtonComponent>
        </form>
      </FormProvider>
      <div
        className={twMerge(
          'text-med-gray-3 font-medium mt-30',
          !isVerified && 'hidden',
        )}
      >
        <p>Patient name: {user?.displayName} </p>
        <p>
          Date/time:{' '}
          {moment(userAgreements?.user_agreements?.[0]?.created_at).format(
            FULL_DATE,
          )}
        </p>
        <p>
          Electronic signature or checkbox audit ID:{' '}
          {userAgreements?.user_agreements?.[0]?.id}
        </p>
      </div>
    </div>
  );
};

export default ConsentToUseAndDiscloseSubstance;
